import React, { useEffect, useState, useRef } from 'react';
import { useQuery } from '@apollo/react-hooks';

import './desktop-product-list-section-style.scss';

import NoProduct from '@src/componentsNew/NoProduct/NoProduct';
import DesktopVerticalProductThumbnailSkeleton from '@src/componentsNew/Skeleton/desktop/components/DesktopVerticalProductThumbnailSkeleton';
import DesktopProductListSkeleton from '@src/componentsNew/Skeleton/desktop/components/DesktopProductListSkeleton';
import VerticalProductThumbnail from '@src/componentsNew/ProductThumbnail/VerticalProductThumbnail/VerticalProductThumbnail';

import { PRODUCT_LIST } from '@src/components/ProductPage/ProductList/queries';
import { products } from '@components/ProductPage/ProductList/__generated__/products';

import { gaV4GetItemListIdFromInitialVariables, gaV4GetItemsFromProducts, gaV4ViewItemList } from '@utils';
import { ProductListBaseProps } from '@src/componentsNew/ProductListTemplate/type';
import { DESKTOP_PRODUCT_LIST_REQUEST_COUNT } from '@src/utils/constants';

const DesktopProductListSection = ({ variables, setProductTotalCount }: ProductListBaseProps) => {
  const fetchingRef = useRef(false);
  const hasNextPageRef = useRef(false);
  const prevProductsNum = useRef(0);

  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);

  const { data, loading, fetchMore } = useQuery<products>(PRODUCT_LIST, {
    variables: variables,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const products = data?.products;

      const productTotalCount = products?.totalCount ?? 0;
      setProductTotalCount(productTotalCount);

      hasNextPageRef.current = products?.pageInfo?.hasNextPage;
      prevProductsNum.current = products?.edges?.length;

      const itemListId = gaV4GetItemListIdFromInitialVariables(variables);
      const items = gaV4GetItemsFromProducts(data?.products, itemListId);
      gaV4ViewItemList(items, itemListId);
    },
  });

  useEffect(() => {
    const handleOnScroll = async () => {
      const scrollTop = (document.documentElement && document.documentElement.scrollTop) || document.body.scrollTop;
      const scrollHeight =
        (document.documentElement && document.documentElement.scrollHeight) || document.body.scrollHeight;
      const clientHeight = document.documentElement.clientHeight || window.innerHeight;
      const scrolledToBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight * 0.75;

      if (scrolledToBottom && hasNextPageRef.current && !fetchingRef.current) {
        fetchingRef.current = true;
        setFetchMoreLoading(true);

        const newVariables = {
          ...variables,
          cursor: data.products.pageInfo.endCursor,
        };

        if (prevProductsNum.current !== data.products.edges.length) {
          fetchingRef.current = false;
          setFetchMoreLoading(false);

          return;
        }

        await fetchMore({
          variables: newVariables,
          updateQuery: (prevResult: products, { fetchMoreResult }) => {
            const products = fetchMoreResult?.products;
            const newEdges = products?.edges;
            const pageInfo = products?.pageInfo;
            const totalCount = products?.totalCount;

            hasNextPageRef.current = pageInfo?.hasNextPage;
            prevProductsNum.current += newEdges?.length;

            const itemListId = gaV4GetItemListIdFromInitialVariables(variables);
            const items = gaV4GetItemsFromProducts(products, itemListId);
            gaV4ViewItemList(items, itemListId);

            return newEdges.length
              ? {
                  products: {
                    __typename: prevResult.products.__typename,
                    edges: [...prevResult.products.edges, ...newEdges],
                    pageInfo,
                    totalCount,
                  },
                }
              : prevResult;
          },
        });

        fetchingRef.current = false;
        setFetchMoreLoading(false);
      }
    };

    document.addEventListener('scroll', handleOnScroll);

    return () => {
      document.removeEventListener('scroll', handleOnScroll);
    };
  }, [data]);

  if (loading) {
    return (
      <DesktopProductListSkeleton type="horizontal" count={DESKTOP_PRODUCT_LIST_REQUEST_COUNT} hasPadding={true} />
    );
  }

  return data?.products?.totalCount > 0 ? (
    <div id="desktop-product-list-section-wrapper">
      {data.products.edges.map((edge, idx) => (
        <VerticalProductThumbnail key={idx} product={edge.node} isVerticalOne={true} />
      ))}
      {fetchMoreLoading &&
        Array.from({ length: DESKTOP_PRODUCT_LIST_REQUEST_COUNT }).map((_, index) => (
          <DesktopVerticalProductThumbnailSkeleton key={index} />
        ))}
    </div>
  ) : (
    <NoProduct />
  );
};

export default DesktopProductListSection;
